import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  BanknotesIcon,
  CalendarIcon,
  ChartBarIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import { getTournamentEvents, getPlayers, getFinancialSummary } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Tournament } from '../components/TournamentList';
import TournamentDetails from '../components/TournamentDetails';
import TournamentModal from '../components/TournamentModal';
import TournamentCategoriesRounds from '../components/TournamentCategoriesRounds';

interface TournamentDashboardProps {
  tournament: Tournament;
  onBack: () => void;
}

export default function TournamentDashboard({ tournament, onBack }: TournamentDashboardProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [stats, setStats] = useState({
    players: {
      total: 0,
      registered: 0,
      pending: 0
    },
    events: {
      total: 0,
      upcoming: 0,
      completed: 0
    },
    finances: {
      revenue: 0,
      expenses: 0,
      balance: 0
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [tournament.id]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch events
      const { data: eventsData, error: eventsError } = await getTournamentEvents(tournament.id);
      if (eventsError) throw eventsError;
      
      // Fetch players
      const { data: playersData, error: playersError } = await getPlayers();
      if (playersError) throw playersError;
      
      // Fetch financial summary
      const { data: financialData, error: financialError } = await getFinancialSummary(tournament.id);
      if (financialError) throw financialError;
      
      // Calculate stats
      const now = new Date();
      const totalEvents = eventsData?.length || 0;
      const upcomingEvents = eventsData?.filter(e => new Date(e.date) > now).length || 0;
      const completedEvents = eventsData?.filter(e => new Date(e.date) <= now).length || 0;
      
      const tournamentPlayers = playersData?.filter(p => p.tournament_id === tournament.id) || [];
      const totalPlayers = tournamentPlayers.length;
      const registeredPlayers = tournamentPlayers.filter(p => p.status === 'Active').length;
      const pendingPlayers = tournamentPlayers.filter(p => p.status === 'Pending').length;
      
      const revenue = financialData?.total_revenue || 0;
      const expenses = financialData?.total_expenses || 0;
      const balance = revenue - expenses;
      
      setStats({
        players: {
          total: totalPlayers,
          registered: registeredPlayers,
          pending: pendingPlayers
        },
        events: {
          total: totalEvents,
          upcoming: upcomingEvents,
          completed: completedEvents
        },
        finances: {
          revenue,
          expenses,
          balance
        }
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleEditTournament = () => {
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <TournamentDetails 
        tournament={tournament} 
        onBack={onBack}
        onEdit={handleEditTournament}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Players Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-blue-500/10 p-3 rounded-xl">
              <UserGroupIcon className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Players</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={stats.players.total}
                  duration={2}
                  className="text-2xl font-bold text-golf-gray"
                />
                <span className="text-sm text-gray-500">
                  ({stats.players.registered} registered)
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.players.pending}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Capacity</div>
              <div className="text-lg font-semibold text-golf-gray">{tournament.capacity}</div>
            </div>
          </div>
        </motion.div>

        {/* Events Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-orange-500/10 p-3 rounded-xl">
              <CalendarIcon className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Events</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={stats.events.total}
                  duration={2}
                  className="text-2xl font-bold text-golf-gray"
                />
                <span className="text-sm text-gray-500">
                  total
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Upcoming</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.events.upcoming}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.events.completed}</div>
            </div>
          </div>
        </motion.div>

        {/* Revenue Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-green-500/10 p-3 rounded-xl">
              <BanknotesIcon className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Revenue</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={stats.finances.revenue}
                  duration={2}
                  formattingFn={(value) => formatCurrency(value)}
                  className="text-2xl font-bold text-golf-gray"
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Progress</span>
              <span className="text-gray-500">
                {((stats.finances.revenue / tournament.prize_pool) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 rounded-full h-2"
                style={{
                  width: `${Math.min((stats.finances.revenue / tournament.prize_pool) * 100, 100)}%`
                }}
              />
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Target: {formatCurrency(tournament.prize_pool)}
            </div>
          </div>
        </motion.div>

        {/* Expenses Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-red-500/10 p-3 rounded-xl">
              <ChartBarIcon className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Expenses</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={stats.finances.expenses}
                  duration={2}
                  formattingFn={(value) => formatCurrency(value)}
                  className="text-2xl font-bold text-golf-gray"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Net Balance</div>
              <div className={`text-lg font-semibold ${stats.finances.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(stats.finances.balance)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:border-golf-orange transition-colors text-left"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-golf-orange/10 p-3 rounded-xl">
              <UserGroupIcon className="w-6 h-6 text-golf-orange" />
            </div>
            <div>
              <p className="font-medium text-golf-gray">Manage Players</p>
              <p className="text-sm text-gray-500">View and edit player registrations</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:border-golf-orange transition-colors text-left"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-golf-orange/10 p-3 rounded-xl">
              <CalendarIcon className="w-6 h-6 text-golf-orange" />
            </div>
            <div>
              <p className="font-medium text-golf-gray">Schedule Events</p>
              <p className="text-sm text-gray-500">Add or modify tournament events</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:border-golf-orange transition-colors text-left"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-golf-orange/10 p-3 rounded-xl">
              <BanknotesIcon className="w-6 h-6 text-golf-orange" />
            </div>
            <div>
              <p className="font-medium text-golf-gray">Financial Overview</p>
              <p className="text-sm text-gray-500">Track revenue and expenses</p>
            </div>
          </div>
        </motion.button>
      </div>

      {/* Categories & Golf Rounds */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-golf-orange/10 p-2 rounded-lg">
            <TagIcon className="w-5 h-5 text-golf-orange" />
          </div>
          <div>
            <h3 className="font-semibold text-golf-gray text-lg">Categories & Golf Rounds</h3>
            <p className="text-sm text-gray-500">Define competition categories and tournament rounds</p>
          </div>
        </div>
        <TournamentCategoriesRounds tournamentId={tournament.id} />
      </motion.div>

      {/* Edit Tournament Modal */}
      <TournamentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        tournament={tournament}
        onSuccess={() => {
          setIsEditModalOpen(false);
          // Refresh tournament data
          onBack(); // Go back to list to refresh
        }}
      />
    </div>
  );
}